import * as THREE from 'three';
import { RTSCamera } from '../engine/camera/rtsCamera';
import { MAX_COMMAND_IDS, type Command, type FormationType, type Stance } from '../engine/simulation/commands';
import { FX_ONE } from '../engine/simulation/fixed';
import { Simulation, TICK_MS, type GameConfig } from '../engine/simulation/simulation';
import { canPlace, repairCost } from '../engine/simulation/systems/construction';
import { canCapture } from '../engine/simulation/systems/capture';
import { mayEnter } from '../engine/simulation/systems/garrison';
import { resourceOf, isFarm } from '../engine/simulation/systems/economy';
import { researchableAt, techAvailability, trainableAt, unitAvailability, canAfford } from '../engine/simulation/systems/production';
import { playerScore } from '../engine/simulation/systems/victory';
import { Order, UState, type SimEvent } from '../engine/simulation/world';
import { GameRenderer } from '../engine/rendering/renderer';
import type { GraphicsSettings } from '../engine/rendering/settings';
import { CIVS } from '../gameplay/civilisations';
import { ALL_TECHS, Cat, KINDS, kindIndex } from '../gameplay/registry';
import { AGE_NAMES, C, RES_NAMES } from '../gameplay/types';
import { BUILDINGS } from '../gameplay/buildings';
import { planWall, wallKindsForAge } from '../gameplay/walls';
import type { CommandTransport } from '../networking/transport';
import { useApp, type CommandButton, type HudState, type SelectionEntry } from '../ui/store';
import type { AudioEngine } from '../engine/audio/audio';
import { InputController } from '../engine/input/input';
import { savePlayerGame } from '../persistence/saves';
import { MatchHistory, victoryDetail } from './matchHistory';

/** Villagers and fishing boats: units that count for the idle-worker button. */
function isWorker(kind: number): boolean {
  const u = KINDS[kind].unit!;
  return (u.classes & C.Villager) !== 0 || (!!u.gatherRates && (u.classes & C.Ship) !== 0);
}

export type ClientMode = 'normal' | 'place' | 'attackMove' | 'patrol' | 'rally' | 'guard';

/** Build menu hotkeys, active after B (or the Build button) while villagers are selected. */
export const BUILD_HOTKEYS: Record<string, string> = {
  house: 'H', farm: 'F', lumberCamp: 'L', miningCamp: 'N', mill: 'M', townCenter: 'T', dock: 'D', market: 'K',
  barracks: 'B', archeryRange: 'A', stable: 'S', blacksmith: 'P', watchtower: 'O', guardTower: 'G', wall: 'W',
  castle: 'C', siegeWorkshop: 'V', university: 'U', monastery: 'Y', wonder: 'Z', arsenal: 'R', foundry: 'X',
  industrialWorkshop: 'I', logisticsDepot: 'J',
};

const ECO_BUILDINGS = ['house', 'farm', 'lumberCamp', 'miningCamp', 'mill', 'market', 'townCenter', 'industrialWorkshop', 'logisticsDepot', 'wonder'];

/**
 * Owns a running match on the client: simulation stepping, rendering,
 * input, selection, HUD snapshots and audio hooks.
 */
/** Most of a frame (ms) the simulation may spend catching up before the frame is drawn. */
const SIM_FRAME_BUDGET_MS = 30;

export class GameClient {
  readonly sim: Simulation;
  readonly renderer: GameRenderer;
  readonly cam: RTSCamera;
  readonly input: InputController;
  readonly local: number;
  selection = new Set<number>();
  groups: number[][] = Array.from({ length: 10 }, () => []);
  mode: ClientMode = 'normal';
  placeKind = -1;
  placeTile: [number, number] = [0, 0];
  /** Wall tool: true while placing a wall line; wallStart is the first click (null before it). */
  wallMode = false;
  wallStart: [number, number] | null = null;
  formation: FormationType = 'line';
  buildMenu = false;
  speed = 1;
  /** The match's chosen game speed (speed returns here after any temporary change). */
  readonly baseSpeed: number;
  paused = false;
  private acc = 0;
  private last = 0;
  private raf = 0;
  private hudTimer = 0;
  private fogTick = -1;
  private lastAlert: { x: number; z: number } | null = null;
  private running = false;
  private replayCommands: Map<number, import('../engine/simulation/commands').CommandEnvelope[]> | null = null;
  /** Frame timing samples for the performance overlay. */
  readonly frameTimes: number[] = [];
  private fpsShareTimer = 0;
  simMs = 0;
  onGameOver: ((won: boolean) => void) | null = null;
  audio: AudioEngine | null = null;
  replayTotal = 0;
  /** The match as it went, for the results screen. */
  readonly history = new MatchHistory();
  /** How the match ended (from the victory event). */
  victory: { team: number; reason: string } | null = null;
  /** Economic-victory warnings already given ("player:percent"). */
  private econWarned = new Set<string>();

  constructor(
    readonly canvas: HTMLCanvasElement,
    simOrConfig: Simulation | GameConfig,
    readonly transport: CommandTransport,
    settings: GraphicsSettings,
  ) {
    this.sim = simOrConfig instanceof Simulation ? simOrConfig : new Simulation(simOrConfig);
    this.baseSpeed = this.sim.config.gameSpeed ?? 1;
    this.speed = this.baseSpeed;
    this.local = transport.localPlayer;
    transport.attach(this.sim);
    const size = this.sim.map.size;
    this.cam = new RTSCamera(canvas.clientWidth / Math.max(1, canvas.clientHeight));
    this.cam.setBounds(size);
    this.renderer = new GameRenderer(canvas, this.sim.world, this.cam, settings);
    this.renderer.viewer = this.local;
    if (this.local < 0 || this.sim.config.revealMap) this.renderer.revealAll = this.local < 0;
    this.renderer.selection = this.selection;
    const start = this.local >= 0 ? this.sim.map.starts[this.local] : { tx: size / 2, ty: size / 2 };
    this.cam.jumpTo(start.tx + 0.5, start.ty + 5, true);
    this.sim.onEvents = (ev) => this.handleEvents(ev);
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      useApp.getState().notify({ text: 'Graphics context lost; waiting for the browser to restore it…', kind: 'warn' });
    });
    canvas.addEventListener('webglcontextrestored', () => {
      this.renderer.updateFog(true);
      useApp.getState().notify({ text: 'Graphics restored.', kind: 'good' });
    });
    this.input = new InputController(this);
    const lt = transport as CommandTransport & { onTakeover?: ((p: number) => void) | null; onChat?: ((p: number, t: string, x?: number, z?: number) => void) | null };
    if ('onTakeover' in lt) {
      lt.onTakeover = (p) => useApp.getState().notify({ text: `${this.sim.world.players[p].name} disconnected; the AI has taken over.`, kind: 'warn' });
      lt.onChat = (p, text, x, z) => {
        useApp.getState().notify({ text: `${this.sim.world.players[p].name}: ${text}`, kind: x !== undefined ? 'attack' : 'info', x, z });
        if (x !== undefined && z !== undefined) this.renderer.particles.burst('flash', x, this.renderer.heights.at(x, z) + 0.5, z, 3, 0, 0);
      };
    }
    this.resize();
    this.renderer.warmUp();
  }

  /** Load a replay: commands are injected tick by tick from the recording. */
  setReplay(commands: import('../engine/simulation/commands').CommandEnvelope[], finalTick = 0): void {
    this.replayCommands = new Map();
    // The recording runs to its final tick (the end of the match), past the last command.
    this.replayTotal = finalTick;
    for (const c of commands) {
      const l = this.replayCommands.get(c.tick) ?? [];
      l.push(c);
      this.replayCommands.set(c.tick, l);
      this.replayTotal = Math.max(this.replayTotal, c.tick);
    }
  }

  private bgTimer: ReturnType<typeof setInterval> | undefined;
  private onVisibility = () => {
    // Browsers pause requestAnimationFrame in background tabs. In multiplayer the
    // simulation must keep pace with peers, so step from a timer while hidden.
    clearInterval(this.bgTimer);
    if (document.hidden && this.transport.networked) {
      this.bgTimer = setInterval(() => this.frame(performance.now(), true), 50);
    }
  };

  /** Multiplayer chat; `flare` marks a map position for allies. */
  chat(text: string, alliesOnly: boolean, flare?: { x: number; z: number }): void {
    const t = this.transport as CommandTransport & { sendChat?: (t: string, a: boolean, x?: number, z?: number) => void };
    t.sendChat?.(text, alliesOnly, flare?.x, flare?.z);
    useApp.getState().notify({ text: `You${alliesOnly ? ' (allies)' : ''}: ${text}`, kind: 'info', x: flare?.x, z: flare?.z });
  }

  /** Replays: advance the simulation quickly without rendering. */
  fastForward(ticks: number): void {
    const end = Math.min(this.sim.tick + ticks, this.replayTotal || Infinity);
    while (this.sim.tick < end && !this.sim.world.gameOver) {
      const next = this.sim.tick + 1;
      if (this.replayCommands) for (const c of this.replayCommands.get(next) ?? []) this.sim.schedule(c);
      this.sim.step();
    }
    this.renderer.updateFog(true);
  }

  start(): void {
    document.addEventListener('visibilitychange', this.onVisibility);
    this.running = true;
    this.last = performance.now();
    const loop = (t: number) => {
      if (!this.running) return;
      this.frame(t);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop(): void {
    document.removeEventListener('visibilitychange', this.onVisibility);
    clearInterval(this.bgTimer);
    this.running = false;
    cancelAnimationFrame(this.raf);
    this.input.dispose();
    this.transport.dispose();
    this.renderer.dispose();
  }

  resize(): void {
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    this.renderer.resize(w, h);
  }

  private frame(now: number, background = false): void {
    // Clamp long frames (tab backgrounding) so we never spiral.
    const dt = Math.min(0.5, (now - this.last) / 1000);
    this.last = now;
    this.frameTimes.push(dt * 1000);
    if (this.frameTimes.length > 240) this.frameTimes.shift();
    // Share our frame rate with the other players about once a second (connection panel).
    if (this.transport.setLocalFps && (this.fpsShareTimer += dt) > 1) {
      this.fpsShareTimer = 0;
      const recent = this.frameTimes.slice(-60);
      this.transport.setLocalFps(1000 / (recent.reduce((a, b) => a + b, 0) / recent.length));
    }
    this.input.update(dt);
    if (!this.paused && !this.sim.world.gameOver) {
      this.acc += dt * 1000 * this.speed;
      // Catch up on game time by a time budget rather than a fixed number of ticks, so when frames
      // slow down (big armies on screen) the game still runs at its proper speed and orders play out
      // at once, instead of everything moving in slow motion. Only if the simulation itself cannot
      // keep up within the budget does the game slow down.
      let steps = 0;
      const t0 = performance.now();
      const budgetMs = Math.max(SIM_FRAME_BUDGET_MS, dt * 1000 * 0.5);
      const maxSteps = Math.max(4, Math.ceil(this.speed * 3), Math.ceil((dt * 1000 * this.speed) / TICK_MS) + 1);
      while (this.acc >= TICK_MS && steps < maxSteps && (steps < 2 || performance.now() - t0 < budgetMs)) {
        const next = this.sim.tick + 1;
        if (!this.transport.canAdvance(next)) {
          this.acc = Math.min(this.acc, TICK_MS);
          break;
        }
        if (this.replayCommands) for (const c of this.replayCommands.get(next) ?? []) this.sim.schedule(c);
        this.sim.step();
        this.transport.afterTick(this.sim.tick);
        this.acc -= TICK_MS;
        steps++;
      }
      if (this.acc >= TICK_MS * 2) this.acc = Math.min(this.acc, TICK_MS * 2);
      if (steps > 0) this.simMs = (performance.now() - t0) / steps;
    }
    if (background) return;
    const alpha = this.paused ? 1 : Math.min(1, this.acc / TICK_MS);
    // Refresh fog once per vision update (every 4 ticks). Compare 4-tick periods rather than testing
    // tick % 4, which a slow frame advancing several ticks at a time could step over indefinitely.
    if (this.sim.tick >> 2 !== this.fogTick) {
      this.fogTick = this.sim.tick >> 2;
      this.renderer.updateFog();
    }
    this.cam.update(dt);
    this.renderer.setWallPreview(this.mode === 'place' && this.wallMode ? this.wallPreview() : null);
    const placement = this.mode === 'place' && this.placeKind >= 0 && !this.wallMode
      ? { kind: this.placeKind, tx: this.placeTile[0], ty: this.placeTile[1], valid: canPlace(this.sim.world, this.placeKind, this.placeTile[0], this.placeTile[1], this.local) }
      : null;
    if (this.sim.victoryState.points.length) this.renderer.setControlPoints(this.sim.victoryState.points);
    const rallyOf = this.primarySlot();
    // Resize (if dynamic resolution changes) before drawing: resizing a canvas clears it, so doing it
    // after the draw would present one blank frame.
    this.updateDynamicResolution(dt);
    // A paused game looks paused: animations, smoke and particles stop with the clock.
    this.renderer.render(this.paused ? 0 : dt, alpha, placement, rallyOf >= 0 && this.sim.world.owner[rallyOf] === this.local ? rallyOf : -1);
    this.audio?.setListener(this.cam.target.x, this.cam.target.z, this.cam.distance);
    this.hudTimer += dt;
    if (this.hudTimer > 0.12) {
      this.hudTimer = 0;
      useApp.getState().setHud(this.buildHud());
    }
  }

  private dynTimer = 0;
  /** Seconds since the match started; resolution is left alone while shaders compile and things settle. */
  private dynWarmup = 0;
  private dynSum = 0;
  private dynFrames = 0;
  /** Steady windows in a row at full speed, whether the last change was a step up, and seconds before trying up again. */
  private dynGood = 0;
  private dynRaised = false;
  private dynHold = 0;

  /**
   * Dynamic resolution: average frame time over ~1.5 s windows; step the render resolution down
   * when slower than ~55 fps, and back up after two steady windows at 58 fps or better.
   *
   * It used to step up only above 75 fps, which a 60 Hz screen never shows (frames are paced to the
   * display), so one heavy moment left the resolution low for the rest of the match. If a step up
   * proves too much (the next window falls below 55 fps), it steps back down and waits 30 seconds
   * before trying again, so it does not see-saw.
   */
  private updateDynamicResolution(dt: number): void {
    if (this.renderer.settings.dynamicResolution === false) return;
    if ((this.dynWarmup += dt) < 5) return;
    this.dynTimer += dt;
    this.dynSum += dt;
    this.dynFrames++;
    if (this.dynTimer < 1.5) return;
    const avg = this.dynSum / this.dynFrames;
    this.dynTimer = this.dynSum = this.dynFrames = 0;
    if (document.hidden) return;
    this.dynHold = Math.max(0, this.dynHold - 1.5);
    if (avg > 1 / 55) {
      this.renderer.setDynamicScale(this.renderer.dynamicScale - 0.05);
      if (this.dynRaised) this.dynHold = 30;
      this.dynRaised = false;
      this.dynGood = 0;
    } else if (avg < 1 / 58 && this.renderer.dynamicScale < 1 && this.dynHold <= 0) {
      if (++this.dynGood >= 2) {
        this.renderer.setDynamicScale(this.renderer.dynamicScale + 0.05);
        this.dynRaised = true;
        this.dynGood = 0;
      } else this.dynRaised = false;
    } else {
      this.dynGood = 0;
      this.dynRaised = false;
    }
  }

  // ---------- Commands ----------
  issue(cmd: Command): void {
    if (this.local < 0) return;
    this.transport.submit(cmd);
  }

  selectedIds(): number[] {
    const w = this.sim.world;
    const out: number[] = [];
    for (const id of this.selection) if (w.resolve(id) >= 0) out.push(id);
    return out;
  }

  ownSelectedUnits(): number[] {
    const w = this.sim.world;
    return this.selectedIds().filter((id) => {
      const s = w.resolve(id);
      return w.owner[s] === this.local && w.cat[s] === Cat.Unit;
    });
  }

  primarySlot(): number {
    for (const id of this.selection) {
      const s = this.sim.world.resolve(id);
      if (s >= 0) return s;
    }
    return -1;
  }

  setSelection(ids: number[], add = false): void {
    if (!add) this.selection.clear();
    for (const id of ids) this.selection.add(id);
    this.buildMenu = false;
    if (ids.length > 0) {
      const s = this.sim.world.resolve(ids[0]);
      if (s >= 0) this.audio?.select(this.sim.world, s);
    }
  }

  toggleSelect(id: number): void {
    if (this.selection.has(id)) this.selection.delete(id);
    else this.selection.add(id);
  }

  /** Context-sensitive right click at a ground point (world units) with optional entity under cursor. */
  /** The unit the view is locked to (entity id), or -1. */
  private followId = -1;

  get following(): boolean {
    return this.followId >= 0 && this.cam.follow !== null;
  }

  /**
   * Lock the view to the selected unit, or release it. The lock holds until the unit is deselected
   * or dies (or the view is sent elsewhere on purpose: the minimap, the Town Centre key...).
   */
  toggleFollow(): void {
    if (this.following) {
      this.cam.follow = null;
      this.followId = -1;
      return;
    }
    const w = this.sim.world;
    const s = this.primarySlot();
    if (s < 0 || w.cat[s] !== Cat.Unit) return;
    const id = w.idOf(s);
    this.followId = id;
    this.cam.follow = () => {
      const t = w.resolve(id);
      if (t < 0 || !this.selection.has(id)) {
        this.followId = -1;
        return null;
      }
      return new THREE.Vector3(w.x[t] / FX_ONE, 0, w.y[t] / FX_ONE);
    };
  }

  /** Selected engineers of ours (ids). */
  selectedEngineers(): number[] {
    const w = this.sim.world;
    return this.ownSelectedUnits().filter((id) => KINDS[w.kind[w.resolve(id)]].unit!.classes & C.Engineer);
  }

  rightClick(gx: number, gz: number, target: number, shift: boolean): void {
    const w = this.sim.world;
    let ids = this.ownSelectedUnits();
    // Engineers: an enemy building they can take is commandeered, a damaged one of ours repaired.
    const engineers = target >= 0 ? this.selectedEngineers() : [];
    if (engineers.length > 0) {
      const tid = w.idOf(target);
      let used = false;
      if (canCapture(w, this.local, target)) {
        this.issue({ t: 'capture', ids: engineers, target: tid });
        this.renderer.orderMarker(w.x[target] / FX_ONE, w.y[target] / FX_ONE, 0xe8c45a);
        used = true;
      } else if (w.cat[target] === Cat.Building && w.owner[target] === this.local && w.built[target] && w.hp[target] < w.maxHp[target]) {
        this.issue({ t: 'repair', ids: engineers, target: tid });
        this.renderer.orderMarker(w.x[target] / FX_ONE, w.y[target] / FX_ONE);
        used = true;
      }
      if (used) {
        this.audio?.acknowledge(w, w.resolve(engineers[0]), 'work');
        ids = ids.filter((id) => !engineers.includes(id));
        if (ids.length === 0) return;
      }
    }
    const fx = Math.round(gx * FX_ONE);
    const fy = Math.round(gz * FX_ONE);
    // Loaded transports sent onto land put their cargo ashore there.
    if (target < 0 && ids.length && !w.pathfinder.isBlockedTile(Math.floor(gx), Math.floor(gz))) {
      const loaded = ids.filter((id) => w.cargo[w.resolve(id)] > 0);
      if (loaded.length) {
        this.issue({ t: 'unload', ids: loaded, x: fx, y: fy });
        this.markGround(gx, gz, 0x7cff7c);
        ids = ids.filter((id) => !loaded.includes(id));
        if (ids.length === 0) return;
      }
    }
    // Into one of our transports (only villagers shelter in buildings, with the Garrison button or
    // the Town Bell, so a right-click on the Town Centre never shuts them in).
    if (target >= 0 && w.owner[target] === this.local && w.capacityOf(target) > 0) {
      const ship = w.cat[target] === Cat.Unit;
      const tid = w.idOf(target);
      const enter = ids.filter((id) => {
        const s = w.resolve(id);
        if (!mayEnter(w, s, target)) return false;
        return ship;
      });
      if (enter.length) {
        this.issue({ t: 'garrison', ids: enter, target: tid });
        this.audio?.acknowledge(w, w.resolve(enter[0]), 'move');
        this.renderer.orderMarker(w.x[target] / FX_ONE, w.y[target] / FX_ONE);
        ids = ids.filter((id) => !enter.includes(id));
        if (ids.length === 0) return;
      }
    }
    // Building selected → rally point.
    if (ids.length === 0) {
      const b = this.primarySlot();
      if (b >= 0 && w.cat[b] === Cat.Building && w.owner[b] === this.local) {
        this.issue({ t: 'rally', building: w.idOf(b), x: fx, y: fy, target: target >= 0 ? w.idOf(target) : undefined });
        this.renderer.orderMarker(gx, gz);
      }
      return;
    }
    // Gatherers: villagers and fishing ships (the simulation enforces land/sea resource domains).
    const villagers = ids.filter((id) => {
      const u = KINDS[w.kind[w.resolve(id)]].unit!;
      return u.classes & C.Villager || (u.gatherRates && target >= 0 && resourceOf(w, target) >= 0);
    });
    const others = ids.filter((id) => !villagers.includes(id));
    if (target >= 0) {
      const tid = w.idOf(target);
      const tOwner = w.owner[target];
      if (tOwner >= 0 && w.areEnemies(this.local, tOwner) && w.cat[target] !== Cat.Resource) {
        this.issue({ t: 'attack', ids, target: tid });
        this.audio?.acknowledge(w, w.resolve(ids[0]), 'attack');
        this.renderer.orderMarker(w.x[target] / FX_ONE, w.y[target] / FX_ONE, 0xff3a2a);
        return;
      }
      if (villagers.length > 0) {
        if (resourceOf(w, target) >= 0) {
          this.issue({ t: 'gather', ids: villagers, target: tid });
        } else if (w.cat[target] === Cat.Building && tOwner === this.local) {
          if (!w.built[target] && isFarm(w, target)) this.issue({ t: 'gather', ids: villagers, target: tid });
          else if (!w.built[target] || w.hp[target] < w.maxHp[target]) this.issue({ t: 'repair', ids: villagers, target: tid });
          else this.issue({ t: 'move', ids: villagers, x: fx, y: fy });
        } else this.issue({ t: 'move', ids: villagers, x: fx, y: fy });
        this.audio?.acknowledge(w, w.resolve(villagers[0]), 'work');
      }
      if (others.length > 0) {
        if (w.cat[target] === Cat.Unit && tOwner === this.local) this.issue({ t: 'guard', ids: others, target: tid });
        else this.issue({ t: 'move', ids: others, x: fx, y: fy, formation: this.formation });
      }
      this.markGround(gx, gz, 0x7cff7c);
      return;
    }
    this.issue({ t: 'move', ids, x: fx, y: fy, formation: this.formation, queue: shift });
    this.audio?.acknowledge(w, w.resolve(ids[0]), 'move');
    this.markGround(gx, gz, 0x7cff7c);
  }

  private markGround(x: number, z: number, _c: number): void {
    this.renderer.orderMarker(x, z);
  }

  /** Left click in a targeting mode. Returns true if the click was consumed. */
  modeClick(gx: number, gz: number, target: number, shift: boolean): boolean {
    const w = this.sim.world;
    const fx = Math.round(gx * FX_ONE);
    const fy = Math.round(gz * FX_ONE);
    switch (this.mode) {
      case 'place': {
        if (this.wallMode) {
          this.wallClick(shift);
          return true;
        }
        const [tx, ty] = this.placeTile;
        const vills = this.ownSelectedUnits().filter((id) => KINDS[w.kind[w.resolve(id)]].unit!.classes & C.Villager);
        if (vills.length && canPlace(w, this.placeKind, tx, ty, this.local)) {
          this.issue({ t: 'build', ids: vills, kind: KINDS[this.placeKind].key, tx, ty, queue: shift });
          this.audio?.ui('place');
          if (!shift) this.mode = 'normal';
        } else this.audio?.ui('error');
        return true;
      }
      case 'attackMove':
        this.issue({ t: 'move', ids: this.ownSelectedUnits(), x: fx, y: fy, attackMove: true, formation: this.formation });
        this.mode = 'normal';
        return true;
      case 'patrol':
        this.issue({ t: 'patrol', ids: this.ownSelectedUnits(), x: fx, y: fy });
        this.mode = 'normal';
        return true;
      case 'guard':
        if (target >= 0) this.issue({ t: 'guard', ids: this.ownSelectedUnits(), target: w.idOf(target) });
        this.mode = 'normal';
        return true;
      case 'rally': {
        const b = this.primarySlot();
        if (b >= 0) this.issue({ t: 'rally', building: w.idOf(b), x: fx, y: fy, target: target >= 0 ? w.idOf(target) : undefined });
        this.mode = 'normal';
        return true;
      }
      default:
        return false;
    }
  }

  /** Planned wall pieces from the first click to the cursor, or the start piece alone before it. */
  wallPlan(): [string, number, number][] {
    const age = this.sim.world.players[this.local].age;
    const [ex, ey] = this.placeTile;
    if (!this.wallStart) return [[wallKindsForAge(age).join, ex, ey]];
    return planWall(age, this.wallStart[0], this.wallStart[1], ex, ey);
  }

  /** Preview pieces, marked invalid where blocked or beyond what the player can afford. */
  private wallPreview(): { kind: number; tx: number; ty: number; valid: boolean }[] {
    const w = this.sim.world;
    const p = w.players[this.local];
    const left = p.res.map((r) => Math.floor(r / FX_ONE));
    const out: { kind: number; tx: number; ty: number; valid: boolean }[] = [];
    for (const [key, tx, ty] of this.wallPlan()) {
      if (this.ownWallAt(tx, ty) >= 0) continue;
      const ki = kindIndex(key);
      const cost = p.stats[ki].cost;
      let valid = canPlace(w, ki, tx, ty, this.local) && cost.every((c, r) => left[r] >= c);
      if (valid) for (let r = 0; r < 4; r++) left[r] -= cost[r];
      else valid = false;
      out.push({ kind: ki, tx, ty, valid });
    }
    return out;
  }

  private ownWallAt(tx: number, ty: number): number {
    const w = this.sim.world;
    if (tx < 0 || ty < 0 || tx >= w.size || ty >= w.size) return -1;
    const oid = w.occupant[ty * w.size + tx];
    const o = oid >= 0 ? w.resolve(oid) : -1;
    return o >= 0 && w.alive[o] && w.owner[o] === this.local && KINDS[w.kind[o]].building && KINDS[w.kind[o]].building!.classes & C.Wall ? o : -1;
  }

  /** Lock onto the nearest existing wall piece of ours within two tiles of the cursor. */
  private snapToWall(tx: number, ty: number): [number, number] {
    let best: [number, number] = [tx, ty];
    let bd = Infinity;
    for (let dy = -2; dy <= 2; dy++)
      for (let dx = -2; dx <= 2; dx++) {
        if (this.ownWallAt(tx + dx, ty + dy) < 0) continue;
        const d = dx * dx + dy * dy;
        if (d < bd && d <= 5) {
          bd = d;
          best = [tx + dx, ty + dy];
        }
      }
    return best;
  }

  private wallClick(shift: boolean): void {
    const w = this.sim.world;
    if (!this.wallStart) {
      this.wallStart = [...this.placeTile];
      this.audio?.ui('place');
      return;
    }
    const vills = this.ownSelectedUnits().filter((id) => KINDS[w.kind[w.resolve(id)]].unit!.classes & C.Villager);
    const pieces = this.wallPlan();
    if (!vills.length || pieces.length < 2 || !this.wallPreview().some((p) => p.valid)) {
      this.audio?.ui('error');
      return;
    }
    this.issue({ t: 'buildWall', ids: vills, pieces, queue: shift });
    this.audio?.ui('place');
    // Shift carries on from the end of this run; otherwise the tool closes.
    if (shift) this.wallStart = [...this.placeTile];
    else this.cancelMode();
  }

  private wallInfo(): { name: string; started: boolean; pieces: number; cost: number[] } {
    const p = this.sim.world.players[this.local];
    const cost = [0, 0, 0, 0];
    const prev = this.wallPreview();
    for (const pc of prev) p.stats[pc.kind].cost.forEach((c, r) => (cost[r] += c));
    return { name: wallKindsForAge(p.age).name, started: !!this.wallStart, pieces: prev.length, cost };
  }

  startWallPlacement(): void {
    this.mode = 'place';
    this.wallMode = true;
    this.wallStart = null;
    this.placeKind = kindIndex(wallKindsForAge(this.sim.world.players[this.local].age).join);
  }

  updatePlacement(gx: number, gz: number): void {
    if (this.wallMode) {
      this.placeTile = this.snapToWall(Math.floor(gx), Math.floor(gz));
      return;
    }
    if (this.placeKind < 0) return;
    const size = KINDS[this.placeKind].building!.size;
    this.placeTile = [Math.round(gx - size / 2), Math.round(gz - size / 2)];
  }

  startPlacement(key: string): void {
    this.placeKind = kindIndex(key);
    this.mode = 'place';
  }

  cancelMode(): void {
    // Cancelling a placement goes back to the build menu; cancelling anything else closes it.
    const placing = this.mode === 'place';
    this.mode = 'normal';
    this.wallMode = false;
    this.wallStart = null;
    this.placeKind = -1;
    if (!placing) this.buildMenu = false;
  }

  /** Build menu hotkey: start placing the building bound to this letter. True if the key was used. */
  buildHotkey(letter: string): boolean {
    if (!this.buildMenu) return false;
    const s = this.primarySlot();
    if (s < 0 || this.sim.world.cat[s] !== Cat.Unit || !(KINDS[this.sim.world.kind[s]].unit!.classes & C.Villager)) {
      this.buildMenu = false;
      return false;
    }
    const b = this.buttons().find((x) => x.group === 'build' && x.hotkey === letter);
    if (!b) return false;
    if (b.disabled) this.audio?.ui('error');
    else this.button(b.id);
    return true;
  }

  /** Execute a command card button. */
  button(id: string): void {
    const w = this.sim.world;
    const [kind, arg] = id.split(':');
    const units = this.ownSelectedUnits();
    const prim = this.primarySlot();
    switch (kind) {
      case 'build':
        if (arg === 'wall') this.startWallPlacement();
        else this.startPlacement(arg);
        break;
      case 'buildMenu':
        this.buildMenu = arg === 'open';
        break;
      case 'train':
        if (prim >= 0) {
          // Distribute across all selected buildings of the same kind.
          const bs = this.selectedIds().filter((i) => w.kind[w.resolve(i)] === w.kind[prim] && w.owner[w.resolve(i)] === this.local);
          for (const b of bs.length ? bs : [w.idOf(prim)]) this.issue({ t: 'train', building: b, kind: arg, count: 1 });
          this.audio?.ui('click');
        }
        break;
      case 'research':
        if (prim >= 0) this.issue({ t: 'research', building: w.idOf(prim), tech: arg });
        this.audio?.ui('click');
        break;
      case 'cancel':
        if (prim >= 0) this.issue({ t: 'cancel', building: w.idOf(prim), index: Number(arg) });
        break;
      case 'stop':
        this.issue({ t: 'stop', ids: units });
        break;
      case 'attackMove':
        this.mode = 'attackMove';
        break;
      case 'patrol':
        this.mode = 'patrol';
        break;
      case 'guard':
        this.mode = 'guard';
        break;
      case 'rally':
        this.mode = 'rally';
        break;
      case 'retreat':
        this.issue({ t: 'retreat', ids: units });
        break;
      case 'garrison':
        this.garrisonNearest(units);
        break;
      case 'bell':
        if (prim >= 0) this.ringBell(prim);
        this.audio?.townBell();
        break;
      case 'unload':
      case 'backToWork':
        if (kind === 'backToWork') this.audio?.allClear();
        this.issue({ t: 'unload', ids: this.selectedIds().filter((i) => w.owner[w.resolve(i)] === this.local && w.cargo[w.resolve(i)] > 0), resume: kind === 'backToWork' || undefined });
        break;
      case 'follow':
        this.toggleFollow();
        break;
      case 'delete':
        this.issue({ t: 'delete', ids: this.selectedIds().filter((i) => w.owner[w.resolve(i)] === this.local) });
        this.selection.clear();
        break;
      case 'formation':
        this.formation = arg as FormationType;
        if (units.length > 1) {
          // Re-form in place facing current direction.
          let cx = 0;
          let cy = 0;
          for (const id of units) {
            const s = w.resolve(id);
            cx += w.x[s];
            cy += w.y[s];
          }
          this.issue({ t: 'move', ids: units, x: Math.trunc(cx / units.length), y: Math.trunc(cy / units.length), formation: this.formation });
        }
        break;
      case 'stance':
        this.issue({ t: 'stance', ids: units, stance: arg as Stance });
        break;
      case 'reseed':
        this.issue({ t: 'autoReseed', on: arg === 'on' });
        break;
      case 'trade': {
        const [r, dir] = arg.split('-');
        this.issue({ t: 'trade', res: Number(r), buy: dir === 'buy' });
        break;
      }
      default:
        break;
    }
  }

  /** Send units into the nearest of our buildings that will take them (Town Centres). */
  private garrisonNearest(units: number[]): void {
    const w = this.sim.world;
    if (!units.length) return;
    const first = w.resolve(units[0]);
    let best = -1;
    let bestD = Infinity;
    for (let b = 0; b < w.highSlot; b++) {
      if (!w.alive[b] || w.cat[b] !== Cat.Building || w.owner[b] !== this.local || !mayEnter(w, first, b)) continue;
      const d = w.edgeDist(b, w.x[first], w.y[first]);
      if (d < bestD) {
        bestD = d;
        best = b;
      }
    }
    if (best < 0) {
      useApp.getState().notify({ text: 'No Town Centre to shelter in', kind: 'warn' });
      return;
    }
    this.issue({ t: 'garrison', ids: units, target: w.idOf(best) });
  }

  /** Town bell: every villager of ours within 20 tiles of the building takes shelter in it. */
  private ringBell(b: number): void {
    const w = this.sim.world;
    const r2 = (20 * FX_ONE) ** 2;
    const vills: { id: number; d: number }[] = [];
    for (let s = 0; s < w.highSlot; s++) {
      if (!w.alive[s] || w.cat[s] !== Cat.Unit || w.owner[s] !== this.local || !(KINDS[w.kind[s]].unit!.classes & C.Villager)) continue;
      const dx = w.x[s] - w.x[b];
      const dy = w.y[s] - w.y[b];
      if (dx * dx + dy * dy <= r2) vills.push({ id: w.idOf(s), d: dx * dx + dy * dy });
    }
    const room = w.capacityOf(b) - w.cargo[b];
    const ids = vills.sort((a, c) => a.d - c.d).slice(0, Math.min(room, MAX_COMMAND_IDS)).map((v) => v.id);
    if (ids.length) this.issue({ t: 'garrison', ids, target: w.idOf(b) });
  }

  private repairersOf(id: number): number {
    const w = this.sim.world;
    let n = 0;
    for (let s = 0; s < w.highSlot; s++) if (w.alive[s] && w.order[s] === Order.Repair && w.target[s] === id) n++;
    return n;
  }

  /** Send up to three villagers (idle first, then the nearest gatherers) to repair the selected building. */
  repairSelected(): void {
    const w = this.sim.world;
    const b = this.primarySlot();
    if (b < 0 || w.cat[b] !== Cat.Building || w.owner[b] !== this.local) return;
    const cands: { s: number; d: number }[] = [];
    for (let s = 0; s < w.highSlot; s++) {
      if (!w.alive[s] || w.owner[s] !== this.local || w.cat[s] !== Cat.Unit) continue;
      if (!(KINDS[w.kind[s]].unit!.classes & C.Villager)) continue;
      const o = w.order[s];
      if (o === Order.Build || o === Order.Repair) continue;
      const busy = o === Order.None && !w.moving[s] ? 0 : 1;
      cands.push({ s, d: busy * 1e12 + w.edgeDist(b, w.x[s], w.y[s]) });
    }
    if (cands.length === 0) {
      useApp.getState().notify({ text: 'No villagers available to repair', kind: 'warn' });
      return;
    }
    cands.sort((a, c) => a.d - c.d);
    this.issue({ t: 'repair', ids: cands.slice(0, 3).map((c) => w.idOf(c.s)), target: w.idOf(b) });
  }

  // ---------- Helpers for input ----------
  selectIdleVillager(): void {
    const w = this.sim.world;
    const idle: number[] = [];
    for (let s = 0; s < w.highSlot; s++) {
      if (!w.alive[s] || w.owner[s] !== this.local || w.cat[s] !== Cat.Unit) continue;
      if (!isWorker(w.kind[s])) continue;
      if (w.order[s] === Order.None && !w.moving[s]) idle.push(s);
    }
    if (idle.length === 0) return;
    const cur = this.primarySlot();
    const i = (idle.indexOf(cur) + 1) % idle.length;
    const s = idle[i];
    this.setSelection([w.idOf(s)]);
    this.cam.jumpTo(w.x[s] / FX_ONE, w.y[s] / FX_ONE);
  }

  selectTownCenter(): void {
    const w = this.sim.world;
    const tc = kindIndex('townCenter');
    const list: number[] = [];
    for (let s = 0; s < w.highSlot; s++) if (w.alive[s] && w.owner[s] === this.local && w.kind[s] === tc) list.push(s);
    if (!list.length) return;
    const cur = this.primarySlot();
    const s = list[(list.indexOf(cur) + 1) % list.length];
    this.setSelection([w.idOf(s)]);
    this.cam.jumpTo(w.x[s] / FX_ONE, w.y[s] / FX_ONE);
  }

  jumpToAlert(): void {
    if (this.lastAlert) this.cam.jumpTo(this.lastAlert.x, this.lastAlert.z);
  }

  setGroup(i: number): void {
    this.groups[i] = this.selectedIds().filter((id) => this.sim.world.owner[this.sim.world.resolve(id)] === this.local);
  }

  recallGroup(i: number, add: boolean, center: boolean): void {
    const w = this.sim.world;
    const ids = this.groups[i].filter((id) => w.resolve(id) >= 0);
    this.groups[i] = ids;
    if (!ids.length) return;
    this.setSelection(ids, add);
    if (center) {
      let x = 0;
      let z = 0;
      for (const id of ids) {
        x += w.x[w.resolve(id)];
        z += w.y[w.resolve(id)];
      }
      this.cam.jumpTo(x / ids.length / FX_ONE, z / ids.length / FX_ONE);
    }
  }

  // ---------- Events ----------
  private handleEvents(events: SimEvent[]): void {
    this.renderer.onEvents(events);
    this.audio?.onEvents(this.sim.world, events, this.local);
    const notify = useApp.getState().notify;
    const w = this.sim.world;
    this.history.update(w);
    if (w.tick % 20 === 0) this.warnEconomic();
    for (const e of events) {
      switch (e.e) {
        case 'underAttack':
          if (e.owner === this.local) {
            this.lastAlert = { x: e.x / FX_ONE, z: e.y / FX_ONE };
            notify({ text: e.building ? 'A building is under attack!' : 'Your units are under attack!', kind: 'attack', x: e.x / FX_ONE, z: e.y / FX_ONE });
          }
          break;
        case 'research':
          if (e.owner === this.local && ALL_TECHS[e.tech].advancesTo === undefined) notify({ text: `Research complete: ${ALL_TECHS[e.tech].name}`, kind: 'good' });
          break;
        case 'age':
          this.history.ageReached(e.owner, e.age, w);
          notify({ text: e.owner === this.local ? `You have advanced to the ${AGE_NAMES[e.age]}!` : `${w.players[e.owner].name} advanced to the ${AGE_NAMES[e.age]}.`, kind: e.owner === this.local ? 'good' : 'info' });
          break;
        case 'popCapped':
          if (e.owner === this.local) notify({ text: 'Population limit reached. Build more houses.', kind: 'warn' });
          break;
        case 'depleted':
          if (e.owner === this.local) notify({ text: `${RES_NAMES[e.res]} source depleted.`, kind: 'warn', x: e.x / FX_ONE, z: e.y / FX_ONE });
          break;
        case 'notice':
          if (e.owner === this.local) notify({ text: e.text, kind: 'warn' });
          break;
        case 'built':
          // Wall lines finish piece by piece; announcing each one would flood the feed.
          if (e.owner === this.local && !(KINDS[e.kind].building!.classes & C.Wall)) {
            const s = w.resolve(e.id);
            notify({ text: `${KINDS[e.kind].building!.name} completed.`, kind: 'good', x: s >= 0 ? w.x[s] / FX_ONE : undefined, z: s >= 0 ? w.y[s] / FX_ONE : undefined });
          }
          break;
        case 'captureStart':
          if (e.owner === this.local) notify({ text: `Your ${KINDS[e.kind].building!.name} is being captured! Soldiers within 8 tiles will stop it.`, kind: 'attack', x: e.x / FX_ONE, z: e.y / FX_ONE });
          break;
        case 'captured': {
          const s = w.resolve(e.id);
          const at = s >= 0 ? { x: w.x[s] / FX_ONE, z: w.y[s] / FX_ONE } : {};
          const name = KINDS[e.kind].building!.name;
          if (e.to === this.local) notify({ text: `You captured a ${name}.`, kind: 'good', ...at });
          else if (e.from === this.local) notify({ text: `Your ${name} was captured by ${w.players[e.to].name}.`, kind: 'attack', ...at });
          break;
        }
        case 'defeated':
          notify({ text: `${w.players[e.owner].name} has been defeated.`, kind: e.owner === this.local ? 'attack' : 'info' });
          break;
        case 'saveRequested':
          // Every peer saves the identical state at the same tick.
          void savePlayerGame(this.sim, `${e.name} (MP)`).then(() => notify({ text: `Multiplayer game saved by ${w.players[e.owner].name}.`, kind: 'good' }));
          break;
        case 'victory': {
          this.victory = { team: e.team, reason: e.reason };
          this.history.record(w);
          const won = this.local >= 0 && w.players[this.local].team === e.team;
          // The match is over: lift the fog of war so the whole map can be explored.
          this.renderer.revealAll = true;
          this.renderer.updateFog(true);
          this.onGameOver?.(won);
          break;
        }
        default:
          break;
      }
    }
  }

  /** Warns everyone as a player closes in on an economic victory (75% and 90% of the target). */
  private warnEconomic(): void {
    const v = this.sim.config.victory;
    if (!v.conditions.includes('economic')) return;
    const w = this.sim.world;
    const notify = useApp.getState().notify;
    for (const p of w.players) {
      if (p.defeated) continue;
      const frac = p.gathered.reduce((a, b) => a + b, 0) / FX_ONE / v.economicTarget;
      for (const pct of [90, 75]) {
        const key = `${p.id}:${pct}`;
        if (frac * 100 < pct || this.econWarned.has(key)) continue;
        this.econWarned.add(key);
        // Passing 90% at once also covers 75%.
        if (pct === 90) this.econWarned.add(`${p.id}:75`);
        const target = v.economicTarget.toLocaleString('en');
        notify(p.id === this.local
          ? { text: `You have gathered ${pct}% of the ${target} resources needed for an economic victory.`, kind: 'good' }
          : { text: `${p.name} has gathered ${pct}% of the ${target} resources needed for an economic victory!`, kind: 'warn' });
        break;
      }
    }
  }

  /** The player closest to an economic victory, when that condition is on. */
  private economicLead(): HudState['economic'] {
    const v = this.sim.config.victory;
    if (!v.conditions.includes('economic')) return null;
    const w = this.sim.world;
    let best: HudState['economic'] = null;
    for (const p of w.players) {
      if (p.defeated) continue;
      const gathered = Math.trunc(p.gathered.reduce((a, b) => a + b, 0) / FX_ONE);
      if (!best || gathered > best.gathered) {
        best = { name: p.id === this.local ? 'You' : p.name, color: '#' + p.color.toString(16).padStart(6, '0'), gathered, target: v.economicTarget };
      }
    }
    // Nothing to show until someone has gathered something.
    return best && best.gathered > 0 ? best : null;
  }

  // ---------- HUD ----------
  buildHud(): HudState {
    const w = this.sim.world;
    const viewer = this.local >= 0 ? this.local : 0;
    const p = w.players[viewer];
    let idle = 0;
    const workers = [0, 0, 0, 0];
    for (let s = 0; s < w.highSlot; s++) {
      if (!w.alive[s] || w.owner[s] !== viewer || w.cat[s] !== Cat.Unit) continue;
      if (w.order[s] === Order.Gather) {
        const t = w.resolve(w.target[s]);
        const r = t >= 0 ? resourceOf(w, t) : w.carryRes[s];
        if (r >= 0 && r < 4) workers[r]++;
      }
      if (isWorker(w.kind[s]) && w.order[s] === Order.None && !w.moving[s]) idle++;
    }
    const groups = new Map<number, SelectionEntry>();
    for (const id of this.selection) {
      const s = w.resolve(id);
      // Gone, or gone inside a building or ship.
      if (s < 0 || w.cat[s] === Cat.Held) {
        this.selection.delete(id);
        continue;
      }
      const k = KINDS[w.kind[s]];
      const g = groups.get(w.kind[s]);
      if (g) {
        g.count++;
        g.ids.push(id);
      } else groups.set(w.kind[s], { id, kind: k.key, name: k.unit?.name ?? k.building?.name ?? k.resource!.name, hp: w.hp[s], maxHp: w.maxHp[s], owner: w.owner[s], count: 1, ids: [id] });
    }
    const prim = this.primarySlot();
    let primary: HudState['primary'] = null;
    if (prim >= 0) {
      const k = KINDS[w.kind[prim]];
      const owner = w.owner[prim];
      const st = owner >= 0 ? w.players[owner].stats[w.kind[prim]] : null;
      const atk = k.unit?.attack ?? k.building?.attack;
      const q = w.queues[prim] ?? [];
      primary = {
        id: w.idOf(prim), kind: k.key, name: k.unit?.name ?? k.building?.name ?? k.resource!.name,
        description: k.unit?.description ?? k.building?.description ?? '',
        hp: w.hp[prim], maxHp: w.maxHp[prim], owner, ownerName: owner >= 0 ? w.players[owner].name : 'Gaia',
        attack: st && atk ? `${st.meleeAttack > 0 ? st.meleeAttack + ' melee' : ''}${st.pierceAttack > 0 ? st.pierceAttack + ' pierce' : ''}${k.building && w.cargo[prim] ? `, ${Math.max(2, st.arrows * 2)} arrows` : ''}` : '',
        armor: st ? `${st.meleeArmor}/${st.pierceArmor}` : '',
        range: st && atk && atk.range > 0 ? (st.range / FX_ONE).toFixed(0) : atk ? 'melee' : '',
        los: st ? st.los : 0,
        speed: st && k.unit ? ((st.speed * 20) / FX_ONE).toFixed(2) : '',
        carry: k.unit?.gatherRates && w.carryAmt[prim] > 0 ? `${Math.floor(w.carryAmt[prim] / FX_ONE)} ${RES_NAMES[w.carryRes[prim]]}` : '',
        garrison: w.capacityOf(prim) > 0 && (owner === this.local || w.cargo[prim] > 0) ? `${w.cargo[prim]}/${w.capacityOf(prim)}` : '',
        resourceLeft: k.building?.foodAmount && w.built[prim] ? 'Unlimited food' : k.resource ? `${Math.ceil(w.resAmt[prim] / FX_ONE)} ${RES_NAMES[resourceOf(w, prim)] ?? ''}` : '',
        built: w.cat[prim] === Cat.Building ? (w.built[prim] ? 1 : w.buildProg[prim] / Math.max(1, (st?.buildTicks ?? 1) * 1000)) : 1,
        queue: q.map((it) => ({ label: it.kind >= 0 ? KINDS[it.kind].unit!.name : ALL_TECHS[it.tech].name, key: it.kind >= 0 ? KINDS[it.kind].key : ALL_TECHS[it.tech].key, isTech: it.kind < 0, progress: it.progress / it.total })),
        isOwn: owner === this.local,
        category: k.unit ? 'unit' : k.building ? 'building' : 'resource',
        repair: owner === this.local && k.building && w.built[prim] && w.hp[prim] < w.maxHp[prim]
          ? { cost: repairCost(w, prim), repairers: this.repairersOf(w.idOf(prim)) } : null,
      };
    }
    const vs = this.sim.victoryState;
    return {
      res: w.resourceTotals(p),
      workers,
      pop: p.pop,
      popCap: p.popCap,
      age: p.age,
      ageName: AGE_NAMES[p.age],
      civName: CIVS[p.civ].name,
      time: this.sim.tick / 20,
      paused: this.paused,
      speed: this.speed,
      baseSpeed: this.baseSpeed,
      idleVillagers: idle,
      selection: [...groups.values()],
      primary,
      buttons: this.buttons(),
      mode: this.mode,
      wall: this.mode === 'place' && this.wallMode ? this.wallInfo() : null,
      buildMenu: this.buildMenu,
      gameOver: w.gameOver ? this.gameOverView() : null,
      players: w.players.map((pl) => ({
        id: pl.id, name: pl.name, color: '#' + pl.color.toString(16).padStart(6, '0'), civ: CIVS[pl.civ].name, age: pl.age, defeated: pl.defeated,
        score: playerScore(w, pl.id).total, team: pl.team, gathered: Math.trunc(pl.gathered.reduce((a, b) => a + b, 0) / FX_ONE),
        winner: w.gameOver && pl.team === w.winnerTeam,
      })),
      economic: this.economicLead(),
      network: this.transport.status(),
      netPlayers: this.transport.players?.() ?? null,
      desync: this.transport.desync(),
      replay: this.replayCommands ? { tick: this.sim.tick, total: this.replayTotal, speed: this.speed } : null,
      wonderTimers: [...vs.wonderSince].map(([team, since]) => ({ team, seconds: Math.max(0, this.sim.config.victory.wonderSeconds - (this.sim.tick - since) / 20) })),
    };
  }

  private gameOverView(): NonNullable<HudState['gameOver']> {
    const w = this.sim.world;
    const team = this.victory?.team ?? w.winnerTeam;
    const winners = w.players.filter((p) => p.team === team).map((p) => (p.id === this.local ? 'You' : p.name));
    const reason = this.victory?.reason ?? '';
    return {
      won: this.local >= 0 && w.players[this.local].team === team,
      reason,
      detail: reason ? victoryDetail(reason, this.sim.config.victory) : '',
      winners,
    };
  }

  private buttons(): CommandButton[] {
    const w = this.sim.world;
    if (this.local < 0) return [];
    const p = w.players[this.local];
    const out: CommandButton[] = [];
    const prim = this.primarySlot();
    if (prim < 0 || w.owner[prim] !== this.local) return out;
    const k = KINDS[w.kind[prim]];
    const affordTip = (cost: number[]) => (canAfford(p, cost) ? null : 'Not enough resources');
    if (k.unit) {
      const villagers = k.unit.classes & C.Villager;
      if (villagers) {
        if (!this.buildMenu) {
          out.push({ id: 'buildMenu:open', label: 'Build', icon: 'build', tooltip: 'Construct buildings (B)', hotkey: 'B', group: 'action' });
        } else {
          for (const b of BUILDINGS) {
            if (b.buildable === false) continue;
            const ki = kindIndex(b.key);
            const st = p.stats[ki];
            if (!st.enabled) continue;
            const reason = p.age < b.age ? `Requires ${AGE_NAMES[b.age]}` : b.limit && p.buildingCounts[ki] >= b.limit ? 'Limit reached' : affordTip(st.cost);
            out.push({ id: `build:${b.key}`, label: b.name, icon: b.key, cost: st.cost, disabled: reason, tooltip: `${b.name}: ${b.description}`, hotkey: BUILD_HOTKEYS[b.key], group: 'build' });
          }
          const wk = wallKindsForAge(p.age);
          const segCost = p.stats[kindIndex(wk.segment)].cost;
          out.push({
            id: 'build:wall', label: wk.name, icon: wk.segment, cost: segCost, disabled: affordTip(segCost), hotkey: BUILD_HOTKEYS.wall, group: 'build',
            tooltip: `${wk.name}: click to set the start, then click again to finish. Shift-click to keep going from the end. Snaps onto nearby walls. ${p.age <= 0 ? 'Fences with timber posts' : p.age === 1 ? 'Fences with stone pillars' : 'Stone walls with stone pillars'}; cost shown per section.`,
          });
          out.sort((a, b) => (ECO_BUILDINGS.includes(a.id.slice(6)) ? 0 : 1) - (ECO_BUILDINGS.includes(b.id.slice(6)) ? 0 : 1));
          out.push({ id: 'buildMenu:close', label: 'Back', icon: 'back', tooltip: 'Back (Esc)', group: 'action' });
          return out;
        }
      }
      out.push({ id: 'stop', label: 'Stop', icon: 'stop', tooltip: 'Stop (S)', hotkey: 'S', group: 'action' });
      // Unarmed ships (fishing boats, transports) have no use for fighting orders.
      const unarmedShip = k.unit.classes & C.Ship && !k.unit.attack;
      if (!villagers && !unarmedShip) {
        out.push({ id: 'attackMove', label: 'Attack Move', icon: 'attackMove', tooltip: 'Attack-move: engage enemies on the way (A)', hotkey: 'A', group: 'action' });
        out.push({ id: 'patrol', label: 'Patrol', icon: 'patrol', tooltip: 'Patrol between here and a point (P)', hotkey: 'P', group: 'action' });
        out.push({ id: 'guard', label: 'Guard', icon: 'guard', tooltip: 'Guard a friendly unit or building (G)', hotkey: 'G', group: 'action' });
        for (const f of ['line', 'column', 'box', 'wedge', 'square', 'loose'] as FormationType[]) {
          out.push({ id: `formation:${f}`, label: f[0].toUpperCase() + f.slice(1), icon: `f-${f}`, tooltip: FORMATION_TIPS[f], group: 'formation', disabled: this.formation === f ? 'Active' : null });
        }
        for (const s of ['aggressive', 'defensive', 'hold', 'passive'] as Stance[]) {
          out.push({ id: `stance:${s}`, label: s[0].toUpperCase() + s.slice(1), icon: `s-${s}`, tooltip: STANCE_TIPS[s], group: 'stance' });
        }
      }
      out.push({ id: 'retreat', label: 'Retreat', icon: 'retreat', tooltip: 'Retreat to the nearest town centre (R)', hotkey: 'R', group: 'action' });
      if (w.capacityOf(prim) > 0) {
        out.push({ id: 'unload', label: 'Unload', icon: 'unload', tooltip: 'Land everyone aboard on the nearest shore. Or right-click the land where they should go ashore.', group: 'action', disabled: w.cargo[prim] ? null : 'Nobody aboard' });
      } else if (w.rules >= 8 && villagers) {
        out.push({ id: 'garrison', label: 'Garrison', icon: 'garrison', tooltip: 'Shelter in the nearest Town Centre.', group: 'action' });
      }
      out.push(this.following
        ? { id: 'follow', label: 'Unfollow', icon: 'follow', tooltip: 'Release the view (F, or deselect the unit)', hotkey: 'F', group: 'action' }
        : { id: 'follow', label: 'Follow', icon: 'follow', tooltip: 'Lock the view to this unit until it is deselected (F)', hotkey: 'F', group: 'action' });
      out.push({ id: 'delete', label: 'Delete', icon: 'delete', tooltip: 'Delete selected (Del)', group: 'action' });
      return out;
    }
    if (k.building) {
      if (!w.built[prim]) {
        out.push({ id: 'delete', label: 'Cancel', icon: 'delete', tooltip: 'Cancel construction (refunds unbuilt portion)', group: 'action' });
        return out;
      }
      for (const ui of trainableAt(w, p, prim)) {
        const u = KINDS[ui].unit!;
        const reason = unitAvailability(w, p, prim, ui) ?? affordTip(p.stats[ui].cost);
        const hotkey = u.key === 'villager' ? 'V' : undefined;
        out.push({ id: `train:${u.key}`, label: u.name, icon: u.key, cost: p.stats[ui].cost, disabled: reason, tooltip: `${u.name}: ${u.description} HP ${p.stats[ui].hp}, armour ${p.stats[ui].meleeArmor}/${p.stats[ui].pierceArmor}.${hotkey ? ' (V; Shift+V for five)' : ''}`, hotkey, group: 'train' });
      }
      for (const ti of researchableAt(w, p, prim)) {
        const t = ALL_TECHS[ti];
        const reason = techAvailability(w, p, ti) ?? affordTip(p.techStats[ti].cost);
        out.push({ id: `research:${t.key}`, label: t.name, icon: t.advancesTo !== undefined ? `age${t.advancesTo}` : 'tech', cost: p.techStats[ti].cost, disabled: reason, tooltip: `${t.name}: ${t.description} (${Math.round(p.techStats[ti].ticks / 20)}s)`, group: t.advancesTo !== undefined ? 'age' : 'research' });
      }
      if (k.key === 'market') {
        for (const r of [0, 1, 3]) {
          out.push({ id: `trade:${r}-buy`, label: `Buy ${RES_NAMES[r]}`, icon: 'buy', tooltip: `Buy 100 ${RES_NAMES[r]} for ~${p.prices[r]} gold`, group: 'action' });
          out.push({ id: `trade:${r}-sell`, label: `Sell ${RES_NAMES[r]}`, icon: 'sell', tooltip: `Sell 100 ${RES_NAMES[r]} for ~${Math.trunc((p.prices[r] * 70) / 100)} gold`, group: 'action' });
        }
      }
      if (w.capacityOf(prim) > 0 && w.rules >= 8) {
        out.push({ id: 'bell', label: 'Town Bell', icon: 'bell', tooltip: 'Every villager within 20 tiles takes shelter inside. With villagers inside, the Town Centre fires twice the arrows.', group: 'action' });
        out.push({ id: 'backToWork', label: 'Back to Work', icon: 'work', tooltip: 'Everyone inside comes out and goes back to the job they were doing, where they were doing it.', group: 'action', disabled: w.cargo[prim] ? null : 'Nobody inside' });
        out.push({ id: 'unload', label: 'Ungarrison', icon: 'unload', tooltip: 'Let everyone out, to wait beside the building', group: 'action', disabled: w.cargo[prim] ? null : 'Nobody inside' });
      }
      if (k.building.trains?.length) out.push({ id: 'rally', label: 'Rally Point', icon: 'rally', tooltip: 'Set rally point (or right-click)', group: 'action' });
      out.push({ id: 'delete', label: 'Delete', icon: 'delete', tooltip: 'Delete building', group: 'action' });
    }
    return out;
  }

  /** Used by the minimap. */
  stateLabel(s: number): string {
    const st = this.sim.world.state[s];
    return ['Idle', 'Walking', 'Gathering', 'Returning', 'Building', 'Attacking', 'Healing'][st] ?? '';
  }

  groundUnderMouse(nx: number, ny: number): THREE.Vector3 | null {
    return this.renderer.groundAtNdc(nx, ny);
  }

  get world() {
    return this.sim.world;
  }

  unitState(s: number): UState {
    return this.sim.world.state[s];
  }
}

const FORMATION_TIPS: Record<FormationType, string> = {
  none: 'No formation',
  line: 'Line: wide front, +1 pierce armour. Best for ranged volleys.',
  column: 'Column: +15% speed, -1 melee armour. For marching.',
  box: 'Box: +2 melee armour, halves cavalry bonus damage, -15% speed.',
  wedge: 'Wedge: cavalry deal +20% melee damage.',
  square: 'Defensive square: hollow ring, +2 melee armour vs charges, -15% speed.',
  loose: 'Loose: double spacing, -40% splash damage from artillery.',
};

const STANCE_TIPS: Record<Stance, string> = {
  aggressive: 'Aggressive: engage any enemy in sight.',
  defensive: 'Defensive: engage nearby enemies, return to position.',
  hold: 'Hold position: only attack enemies in range; never move.',
  passive: 'Passive: never attack automatically.',
};
